import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Search, FileText, Tag, MonitorSmartphone, Layers,
  BookOpen, Gauge, Network, Loader2, AlertTriangle, Copy, Check,
} from "lucide-react";
import RelatedTools from "@/components/widgets/RelatedTools";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL || '';

/* ───────────────────────────────────────────────────────────────────────── */
/* Tool registry — single source of truth                                    */
/* ───────────────────────────────────────────────────────────────────────── */
const TOOLS = [
  { key: "keyword-generator", label: "AI Keyword Generator", icon: Search,
    desc: "Generate 30 long-tail keywords with volume tier, difficulty & intent." },
  { key: "content-grader", label: "SEO Content Grader", icon: FileText,
    desc: "Paste a draft → get score, fixes, title/meta suggestions." },
  { key: "meta-tags", label: "Meta Tag Generator", icon: Tag,
    desc: "Title, meta, OG & Twitter tags optimised for your keyword." },
  { key: "serp-simulator", label: "SERP Simulator", icon: MonitorSmartphone,
    desc: "See how your snippet looks in Google + truncation warnings." },
  { key: "keyword-clustering", label: "Keyword Clustering", icon: Layers,
    desc: "Group a list of keywords by topic & intent for content planning." },
  { key: "content-brief", label: "Content Brief Generator", icon: BookOpen,
    desc: "Full SEO brief — outline, H2s, internal links, sources, meta." },
  { key: "readability", label: "Readability Checker", icon: Gauge,
    desc: "Flesch Reading Ease + Claude-powered rewrite tips." },
  { key: "lsi-keywords", label: "LSI Keyword Finder", icon: Network,
    desc: "Semantic terms & entities high-ranking pages use together." },
];

/* ───────────────────────────────────────────────────────────────────────── */
/* Page                                                                       */
/* ───────────────────────────────────────────────────────────────────────── */
export default function AISEOTools() {
  // Support deep-linking via ?tool=<key> so dedup'd AI Studio cards can each open a distinct tool.
  const initialTool = (() => {
    if (typeof window === "undefined") return "keyword-generator";
    const q = new URLSearchParams(window.location.search).get("tool");
    return TOOLS.find((t) => t.key === q) ? q : "keyword-generator";
  })();
  const [active, setActive] = useState(initialTool);

  // Keep URL in sync when the user switches tools in-page
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (active && active !== "keyword-generator") {
      url.searchParams.set("tool", active);
    } else {
      url.searchParams.delete("tool");
    }
    window.history.replaceState({}, "", url.toString());
  }, [active]);

  const Tool = TOOL_FORMS[active];

  return (
    <>
    <Header />
    <div data-testid="ai-seo-tools-page" style={{ background: "#F8FAFC", minHeight: "100vh", color: "#0F172A" }}>
      <header style={{ padding: "40px 24px 24px", maxWidth: 1200, margin: "0 auto" }}>
        <span className="ai-seo-eyebrow"><Sparkles size={14} /> Claude Haiku 4.5 · 100% real LLM</span>
        <h1 style={{ fontSize: 36, margin: "8px 0 6px", fontWeight: 800 }}>AI SEO Toolkit</h1>
        <p style={{ color: "#64748B", margin: 0, maxWidth: 720 }}>
          8 pure-LLM tools. Every result is freshly generated for your exact input — no mock data, no
          recycled examples. Results are cached for 24h on the server to keep your spend low.
        </p>
      </header>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 60px", display: "grid", gap: 18, gridTemplateColumns: "260px 1fr" }}>
        {/* Sidebar */}
        <nav data-testid="ai-seo-nav" style={{ display: "grid", gap: 6, alignContent: "start" }}>
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const on = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`ai-seo-tab-${t.key}`}
                style={{
                  textAlign: "left", padding: "12px 14px", borderRadius: 10,
                  border: `1px solid ${on ? "rgba(167,139,250,0.4)" : "#F8FAFC"}`,
                  background: on ? "linear-gradient(90deg, rgba(167,139,250,0.18), rgba(236,124,94,0.10))" : "#F8FAFC",
                  color: "#0F172A", cursor: "pointer", display: "flex", gap: 10, alignItems: "center",
                }}
              >
                <Icon size={16} style={{ flexShrink: 0, color: on ? "#a78bfa" : "#64748B" }} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Tool form + results */}
        <section data-testid="ai-seo-panel" style={{ padding: 22, background: "#F8FAFC", borderRadius: 14, border: "1px solid #F8FAFC" }}>
          <Tool />
          <RelatedTools category="seo" current={active} />
        </section>
      </div>
    </div>
    <Footer />
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* Shared bits                                                                */
/* ───────────────────────────────────────────────────────────────────────── */
function useTool(endpoint) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [raw, setRaw] = useState(null);

  const run = async (body) => {
    setLoading(true); setError(null); setResult(null); setRaw(null);
    try {
      const r = await axios.post(`${API}/api/ai-seo/${endpoint}`, body, { withCredentials: true });
      setResult(r.data?.data || r.data);
      setRaw(r.data || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to fetch data. Please check your input and try again.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setLoading(false); }
  };
  return { loading, error, result, run, raw };
}

function FieldInput({ id, label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 6 }}>{label}</span>
      <input
        id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testId}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: 10,
          background: "#F8FAFC", border: "1px solid #F8FAFC",
          color: "#0F172A", fontSize: 14,
        }}
      />
    </label>
  );
}

function FieldTextarea({ id, label, value, onChange, placeholder, rows = 6, testId }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 6 }}>{label}</span>
      <textarea
        id={id} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testId}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: 10,
          background: "#F8FAFC", border: "1px solid #F8FAFC",
          color: "#0F172A", fontSize: 14, resize: "vertical",
        }}
      />
    </label>
  );
}

function RunButton({ loading, onClick, label, testId }) {
  return (
    <button
      onClick={onClick} disabled={loading} data-testid={testId}
      style={{
        padding: "10px 16px", borderRadius: 10, border: 0, cursor: loading ? "not-allowed" : "pointer",
        background: "linear-gradient(90deg, #a78bfa, #ec7c5e)", color: "white", fontWeight: 700,
        display: "inline-flex", alignItems: "center", gap: 8, opacity: loading ? 0.7 : 1,
      }}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
      {loading ? "Generating…" : label}
    </button>
  );
}

function ErrorBox({ error }) {
  if (!error) return null;
  return (
    <div data-testid="ai-seo-error" style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", display: "flex", gap: 8, alignItems: "flex-start" }}>
      <AlertTriangle size={16} color="#ef4444" style={{ marginTop: 2 }} />
      <small style={{ color: "#EF4444" }}>{error}</small>
    </div>
  );
}

function CopyBtn({ text }) {
  const [c, setC] = useState(false);
  const click = async () => {
    try { await navigator.clipboard.writeText(text); setC(true); toast.success("Copied"); setTimeout(() => setC(false), 1200); }
    catch { toast.error("Couldn't copy"); }
  };
  return (
    <button onClick={click} style={{ background: "none", border: 0, cursor: "pointer", color: "#a78bfa", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
      {c ? <Check size={12} /> : <Copy size={12} />}{c ? " Copied" : " Copy"}
    </button>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 1) Keyword Generator                                                       */
/* ───────────────────────────────────────────────────────────────────────── */
function KwGenForm() {
  const [seed, setSeed] = useState("");
  const [niche, setNiche] = useState("");
  const [country, setCountry] = useState("IN");
  const { loading, error, result, run, raw } = useTool("keyword-generator");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>AI Keyword Generator</h2>
      <p style={{ color: "#64748B", marginTop: -8 }}>
        30 keyword ideas with <strong style={{ color: "#0F172A" }}>REAL search volume</strong> (SerpAPI/Google) plus AI-graded difficulty &amp; intent.
      </p>
      <FieldInput label="Seed keyword *" value={seed} onChange={setSeed} placeholder="e.g. youtube growth" testId="kw-seed" />
      <FieldInput label="Niche" value={niche} onChange={setNiche} placeholder="creators, agency, edtech…" testId="kw-niche" />
      <FieldInput label="Country" value={country} onChange={setCountry} placeholder="US, IN, UK…" testId="kw-country" />
      <RunButton loading={loading} onClick={() => run({ seed_keyword: seed, niche, country })} label="Generate 30 keywords" testId="kw-run" />
      <ErrorBox error={error} />
      {result && Array.isArray(result) && (
        <div data-testid="kw-results" style={{ marginTop: 16, overflowX: "auto" }}>
          {raw && (
            <>
              <div
                data-testid="kw-source-badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  background: raw.real_count > 0 ? "#DCFCE7" : "#FEF3C7",
                  color: raw.real_count > 0 ? "#15803D" : "#92400E",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  marginBottom: 8,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                }}
              >
                {raw.real_count > 0
                  ? `LIVE GOOGLE SERP · ${raw.real_count} real keywords${raw.trends_count > 0 ? ` · ${raw.trends_count} with Trends` : ""}`
                  : "AI ESTIMATES ONLY · live API unavailable"}
              </div>
              {raw.notice && (
                <div style={{ marginBottom: 12, padding: "10px 12px", background: "#F1F5F9", borderRadius: 8, fontSize: 12, color: "#475569", lineHeight: 1.5, border: "1px solid #E2E8F0" }}>
                  <strong style={{ color: "#0F172A" }}>How to read this:</strong> {raw.notice}
                </div>
              )}
            </>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ textAlign: "left", borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ padding: 8 }}>Keyword</th>
              <th style={{ padding: 8 }}>Volume</th>
              <th style={{ padding: 8, textAlign: "right" }}>Trend (12m)</th>
              <th style={{ padding: 8 }}>Competition</th>
              <th style={{ padding: 8, textAlign: "right" }}>KD</th>
              <th style={{ padding: 8 }}>Intent</th>
              <th style={{ padding: 8 }}>Source</th>
            </tr></thead>
            <tbody>
              {result.map((k, i) => {
                const isReal = k.source && k.source !== "llm-estimate";
                const volStr = k.volume || "—";
                const volColor = String(volStr).toLowerCase().includes("high")
                  ? "#15803D"
                  : String(volStr).toLowerCase().includes("medium")
                    ? "#92400E"
                    : isReal ? "#475569" : "#94A3B8";
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }} data-testid={`kw-row-${i}`}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{k.keyword}</td>
                    <td style={{ padding: 8 }}>
                      <span className="kw-pill" style={{ background: isReal ? "#F1F5F9" : "#FAFAFA", color: volColor, fontWeight: 700 }}>
                        {volStr}
                      </span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {k.trend_score != null ? (
                        <span title={`Latest: ${k.trend_latest} · Peak: ${k.trend_peak}`} style={{ color: "#7C3AED", fontWeight: 600 }}>
                          {Math.round(k.trend_score)}/100
                        </span>
                      ) : <span style={{ color: "#CBD5E1" }}>—</span>}
                    </td>
                    <td style={{ padding: 8 }}>
                      {k.competition ? (
                        <span className="kw-pill" style={{
                          background: k.competition === "High" ? "#FEE2E2" : k.competition === "Medium" ? "#FEF3C7" : "#DCFCE7",
                          color: k.competition === "High" ? "#B91C1C" : k.competition === "Medium" ? "#92400E" : "#15803D",
                        }}>{k.competition}</span>
                      ) : <span style={{ color: "#CBD5E1" }}>—</span>}
                    </td>
                    <td style={{ padding: 8, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{k.difficulty ?? "—"}</td>
                    <td style={{ padding: 8 }}><span className="kw-pill">{k.intent}</span></td>
                    <td style={{ padding: 8 }}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: isReal ? "#DCFCE7" : "#F1F5F9",
                        color: isReal ? "#15803D" : "#64748B",
                        textTransform: "uppercase",
                      }}>{isReal ? "Live" : "AI"}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 2) Content Grader                                                          */
/* ───────────────────────────────────────────────────────────────────────── */
function ContentGraderForm() {
  const [content, setContent] = useState("");
  const [kw, setKw] = useState("");
  const { loading, error, result, run } = useTool("content-grader");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>SEO Content Grader</h2>
      <FieldInput label="Target keyword *" value={kw} onChange={setKw} placeholder="e.g. on-page seo checklist" testId="cg-kw" />
      <FieldTextarea label="Content *" value={content} onChange={setContent} rows={10} placeholder="Paste your article, blog post or page copy…" testId="cg-content" />
      <RunButton loading={loading} onClick={() => run({ content, target_keyword: kw })} label="Grade my content" testId="cg-run" />
      <ErrorBox error={error} />
      {result && (
        <div data-testid="cg-results" style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat label="Overall" value={result.overall_score} suffix="/100" />
            <Stat label="Readability" value={result.readability_score} suffix="/100" />
            <Stat label="Words" value={result.word_count} />
            <Stat label="Keyword density" value={result.keyword_density} />
            <Stat label="Sentiment" value={result.sentiment} />
          </div>
          <Section title="Recommendations" items={result.recommendations} />
          <Section title="Issues" items={result.issues} />
          {result.title_tag && <SubScore label="Title tag" obj={result.title_tag} />}
          {result.meta_description && <SubScore label="Meta description" obj={result.meta_description} />}
          {result.heading_structure && <SubScore label="Heading structure" obj={result.heading_structure} />}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, suffix = "" }) {
  return (
    <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.18)" }}>
      <small style={{ color: "#64748B", display: "block" }}>{label}</small>
      <strong style={{ fontSize: 20 }}>{value ?? "—"}{suffix}</strong>
    </div>
  );
}

function Section({ title, items }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <h4 style={{ margin: "6px 0" }}>{title}</h4>
      <ul style={{ paddingLeft: 18, color: "#475569", fontSize: 14, margin: 0 }}>
        {items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
      </ul>
    </div>
  );
}

function SubScore({ label, obj }) {
  return (
    <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#F8FAFC" }}>
      <strong>{label}</strong> · score {obj.score}/100
      {obj.suggestion && <p style={{ margin: "6px 0 0", color: "#475569" }}>{obj.suggestion}</p>}
      {obj.issues?.length > 0 && <ul style={{ paddingLeft: 18, color: "#475569" }}>{obj.issues.map((i, x) => <li key={x}>{i}</li>)}</ul>}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 3) Meta Tag Generator                                                      */
/* ───────────────────────────────────────────────────────────────────────── */
function MetaTagForm() {
  const [topic, setTopic] = useState("");
  const [kw, setKw] = useState("");
  const [biz, setBiz] = useState("");
  const { loading, error, result, run } = useTool("meta-tags");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Meta Tag Generator</h2>
      <FieldInput label="Page topic *" value={topic} onChange={setTopic} placeholder="e.g. complete guide to email marketing" testId="mt-topic" />
      <FieldInput label="Target keyword *" value={kw} onChange={setKw} placeholder="e.g. email marketing tools" testId="mt-kw" />
      <FieldInput label="Business name" value={biz} onChange={setBiz} placeholder="optional — appended to title" testId="mt-biz" />
      <RunButton loading={loading} onClick={() => run({ topic, target_keyword: kw, business_name: biz })} label="Generate meta tags" testId="mt-run" />
      <ErrorBox error={error} />
      {result && (
        <div data-testid="mt-results" style={{ marginTop: 16, display: "grid", gap: 10 }}>
          {[
            ["Title tag", result.title_tag],
            ["Meta description", result.meta_description],
            ["OG title", result.og_title],
            ["OG description", result.og_description],
            ["Twitter title", result.twitter_title],
            ["Twitter description", result.twitter_description],
          ].map(([label, value]) => (
            <div key={label} style={{ padding: 10, borderRadius: 10, background: "#F8FAFC" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <small style={{ color: "#64748B" }}>{label} · {value?.length || 0} chars</small>
                <CopyBtn text={value || ""} />
              </div>
              <div style={{ fontSize: 14 }}>{value}</div>
            </div>
          ))}
          {result.secondary_keywords?.length > 0 && (
            <div>
              <small style={{ color: "#64748B" }}>Secondary keywords:</small>{" "}
              {result.secondary_keywords.map((k, i) => <span key={i} className="kw-pill" style={{ marginRight: 6 }}>{k}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 4) SERP Simulator                                                          */
/* ───────────────────────────────────────────────────────────────────────── */
function SerpSimulatorForm() {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [url, setUrl] = useState("https://example.com/page");
  const [q, setQ] = useState("");
  const { loading, error, result, run } = useTool("serp-simulator");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>SERP Simulator</h2>
      <FieldInput label="Title tag *" value={title} onChange={setTitle} placeholder="Your page title" testId="serp-title" />
      <FieldTextarea label="Meta description *" value={desc} onChange={setDesc} rows={3} placeholder="Your meta description" testId="serp-desc" />
      <FieldInput label="URL *" value={url} onChange={setUrl} placeholder="https://yourdomain.com/page" testId="serp-url" />
      <FieldInput label="Search query (optional)" value={q} onChange={setQ} placeholder="user query to check keyword presence" testId="serp-q" />
      <RunButton loading={loading} onClick={() => run({ title_tag: title, meta_description: desc, url, query: q || undefined })} label="Preview snippet" testId="serp-run" />
      <ErrorBox error={error} />
      {result && (
        <div data-testid="serp-results" style={{ marginTop: 16 }}>
          <div style={{ padding: 16, borderRadius: 10, background: "white", color: "#202124", fontFamily: "arial, sans-serif" }}>
            <div style={{ color: "#202124", fontSize: 14 }}>{result.preview.display_url}</div>
            <div style={{ color: "#1a0dab", fontSize: 20, lineHeight: "26px", margin: "2px 0" }}>{result.preview.title}</div>
            <div style={{ color: "#4d5156", fontSize: 14, lineHeight: "20px" }}>{result.preview.description}</div>
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Stat label="Title chars" value={result.stats.title_chars} suffix="/60" />
            <Stat label="Desc chars" value={result.stats.desc_chars} suffix="/155" />
          </div>
          {result.warnings?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong>Warnings</strong>
              <ul style={{ paddingLeft: 18, margin: "6px 0", color: "#F59E0B" }}>
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 5) Keyword Clustering                                                      */
/* ───────────────────────────────────────────────────────────────────────── */
function ClusterForm() {
  const [list, setList] = useState("");
  const { loading, error, result, run } = useTool("keyword-clustering");

  const submit = () => {
    const keywords = list.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (keywords.length < 2) { toast.error("Add at least 2 keywords"); return; }
    run({ keywords });
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Keyword Clustering</h2>
      <FieldTextarea label="Paste keywords (one per line or comma-separated)" value={list} onChange={setList} rows={10} placeholder={"youtube growth tips\nhow to grow on youtube\nbest video editor\n…"} testId="cluster-list" />
      <RunButton loading={loading} onClick={submit} label="Cluster keywords" testId="cluster-run" />
      <ErrorBox error={error} />
      {result && Array.isArray(result) && (
        <div data-testid="cluster-results" style={{ marginTop: 16, display: "grid", gap: 12 }}>
          {result.map((c, i) => (
            <div key={i} style={{ padding: 12, borderRadius: 10, background: "#F8FAFC" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>{c.topic}</strong>
                <span className="kw-pill">{c.intent}</span>
              </div>
              <small style={{ color: "#64748B" }}>Pillar: {c.pillar_keyword}</small>
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {c.keywords?.map((k, x) => <span key={x} className="kw-pill">{k}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 6) Content Brief                                                           */
/* ───────────────────────────────────────────────────────────────────────── */
function BriefForm() {
  const [kw, setKw] = useState("");
  const [audience, setAudience] = useState("general readers");
  const [tone, setTone] = useState("professional");
  const { loading, error, result, run } = useTool("content-brief");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Content Brief Generator</h2>
      <FieldInput label="Target keyword *" value={kw} onChange={setKw} placeholder="e.g. how to start a podcast" testId="brief-kw" />
      <FieldInput label="Audience" value={audience} onChange={setAudience} placeholder="e.g. solo creators" testId="brief-aud" />
      <FieldInput label="Tone" value={tone} onChange={setTone} placeholder="e.g. friendly, technical, hype…" testId="brief-tone" />
      <RunButton loading={loading} onClick={() => run({ target_keyword: kw, audience, tone })} label="Generate brief" testId="brief-run" />
      <ErrorBox error={error} />
      {result && (
        <div data-testid="brief-results" style={{ marginTop: 16, display: "grid", gap: 12 }}>
          <h3 style={{ margin: 0 }}>{result.working_title}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Stat label="Intent" value={result.search_intent} />
            <Stat label="Target words" value={result.target_word_count} />
          </div>
          <Section title="Outline" items={result.outline?.map((o) => `${o.h2} — ${(o.talking_points || []).join(" · ")}`)} />
          <Section title="Questions to answer" items={result.questions_to_answer} />
          <Section title="Internal link ideas" items={result.internal_link_ideas} />
          <Section title="External sources" items={result.external_source_ideas} />
          <div style={{ padding: 10, borderRadius: 10, background: "#F8FAFC" }}>
            <strong>Meta title</strong><div>{result.meta_title}</div>
            <strong>Meta description</strong><div>{result.meta_description}</div>
            <strong>CTA</strong><div>{result.cta}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 7) Readability                                                             */
/* ───────────────────────────────────────────────────────────────────────── */
function ReadabilityForm() {
  const [content, setContent] = useState("");
  const { loading, error, result, run } = useTool("readability");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Readability Checker</h2>
      <FieldTextarea label="Paste your text *" value={content} onChange={setContent} rows={12} placeholder="Drop a chapter, blog post or landing page copy…" testId="read-content" />
      <RunButton loading={loading} onClick={() => run({ content })} label="Analyse readability" testId="read-run" />
      <ErrorBox error={error} />
      {result && (
        <div data-testid="read-results" style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat label="Flesch Ease" value={result.flesch_reading_ease} suffix={` · ${result.label}`} />
            <Stat label="Grade" value={result.flesch_kincaid_grade} />
            <Stat label="Words" value={result.word_count} />
            <Stat label="Sentences" value={result.sentence_count} />
            <Stat label="Avg sentence" value={result.avg_sentence_length} />
            <Stat label="Syllables/word" value={result.avg_syllables_per_word} />
          </div>
          <Section title="Improvement tips" items={result.tips} />
          {result.rewrites?.length > 0 && (
            <div>
              <h4>Suggested rewrites</h4>
              {result.rewrites.map((r, i) => (
                <div key={i} style={{ padding: 10, marginBottom: 8, borderRadius: 10, background: "#F8FAFC" }}>
                  <small style={{ color: "#F59E0B" }}>Before:</small>
                  <p style={{ margin: "2px 0 8px" }}>{r.original}</p>
                  <small style={{ color: "#10B981" }}>After:</small>
                  <p style={{ margin: "2px 0" }}>{r.improved}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
/* 8) LSI Keywords                                                            */
/* ───────────────────────────────────────────────────────────────────────── */
function LSIForm() {
  const [kw, setKw] = useState("");
  const [niche, setNiche] = useState("");
  const { loading, error, result, run } = useTool("lsi-keywords");

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>LSI Keyword Finder</h2>
      <FieldInput label="Primary keyword *" value={kw} onChange={setKw} placeholder="e.g. content marketing strategy" testId="lsi-kw" />
      <FieldInput label="Niche" value={niche} onChange={setNiche} placeholder="optional — refines semantic context" testId="lsi-niche" />
      <RunButton loading={loading} onClick={() => run({ primary_keyword: kw, niche })} label="Find LSI keywords" testId="lsi-run" />
      <ErrorBox error={error} />
      {result && (
        <div data-testid="lsi-results" style={{ marginTop: 16, display: "grid", gap: 14 }}>
          <PillGroup title="LSI terms" items={result.lsi_terms} />
          <PillGroup title="Entities" items={result.entities} />
          <PillGroup title="Co-occurring phrases" items={result.co_occurring_phrases} />
          <Section title="Questions to address" items={result.questions} />
        </div>
      )}
    </div>
  );
}

function PillGroup({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 style={{ margin: "6px 0" }}>{title}</h4>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.map((i, x) => <span key={x} className="kw-pill">{i}</span>)}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */
const TOOL_FORMS = {
  "keyword-generator": KwGenForm,
  "content-grader": ContentGraderForm,
  "meta-tags": MetaTagForm,
  "serp-simulator": SerpSimulatorForm,
  "keyword-clustering": ClusterForm,
  "content-brief": BriefForm,
  "readability": ReadabilityForm,
  "lsi-keywords": LSIForm,
};
